<template>
  <VFormItem
    v-if="product && offerCharacteristic.isImage" class="characteristic-image"
    :label="offerCharacteristic.name"
    :label-width="labelWidth"
    :layout="layout"
  >
    <div class="image-container">
      <template v-if="picturePointers?.length > 0">
        <VPictureThumbnail
          v-for="pointer in picturePointers"
          :key="pointer.id"
          :entity-pointer="pointer"
        />
      </template>
      <template v-else>
        <div class="no-images">Не прикреплено</div>
      </template>
    </div>
    <template #helper-text>{{ offerCharacteristic.characteristic.description }}</template>
  </VFormItem>

  <VInput
    v-if="offerCharacteristic.isString || offerCharacteristic.isLink"

    :label="offerCharacteristic.name"
    v-model="offerCharacteristic.value"

    :data-desired="product && product.getProductCharacteristicById(offerCharacteristic.characteristic.id)?.valueStringRepresentation"

    :required="offerCharacteristic.validations.isRequired"
    :min-length="offerCharacteristic.validations.minLength"
    :max-length="offerCharacteristic.validations.maxLength"

    :class="{ attention: product && offerCharacteristic.value && offerCharacteristic.getMismatchByProduct(product) }"
    :label-width="labelWidth"
    :layout="layout"
  >
    <template #helper-text>
      {{ offerCharacteristic.characteristic.description }}
    </template>
  </VInput>

  <VInputNumberRus
    v-if="offerCharacteristic.isNumeric"

    :label="offerCharacteristic.name"
    v-model="offerCharacteristic.value"

    :class="{ attention: product && offerCharacteristic.value && offerCharacteristic.getMismatchByProduct(product) }"
    :data-desired="product && product.getProductCharacteristicById(offerCharacteristic.characteristic.id)?.valueStringRepresentation"

    :required="offerCharacteristic.validations.isRequired"
    :min="offerCharacteristic.validations.min"
    :max="offerCharacteristic.validations.max"
    :range="offerCharacteristic.validations.range"
    :precision="offerCharacteristic.validations.precision || 3"

    :label-width="labelWidth"
    :layout="layout"
  >
    <template #helper-text>{{ offerCharacteristic.characteristic.description }}</template>
  </VInputNumberRus>

  <VSelectV2
    v-if="offerCharacteristic.isSelectableSingle"

    :label="offerCharacteristic.name"
    v-model="offerCharacteristic.value"

    :class="{ attention: product && offerCharacteristic.value && offerCharacteristic.getMismatchByProduct(product) }"
    :data-desired="product && product.getProductCharacteristicById(offerCharacteristic.characteristic.id)?.valueStringRepresentation"

    :data="allowedValuesSource"
    :required="offerCharacteristic.validations.isRequired"

    :label-width="labelWidth"
    :layout="layout"
  >
    <template #helper-text>
      {{ offerCharacteristic.characteristic.description }}</template>
  </VSelectV2>

  <VFormItem
    v-if="offerCharacteristic.isSelectableMulti"

    :layout="layout"
    :label="offerCharacteristic.name"
    :label-width="labelWidth"
    :with-asterisk="offerCharacteristic.validations.isRequired"

    class="multiselect"
  >
    <template v-if="emptyListValue">
      <VButtonGroup>
        <VButton
          v-if="offerCharacteristic.validations.isRequired === false"
          :theme="selectableMultiState === 'empty' ? 'primary' : 'default'"
          @click="onCharacteristicEmptyValueChange(null)"
        >
          Не указывать
        </VButton>

        <VButton
          :theme="selectableMultiState === 'none' ? 'primary' : 'default'"
          @click="onCharacteristicEmptyValueChange(true)"
        >
          {{ emptyListValue.name }}
        </VButton>

        <VButton
          :theme="selectableMultiState === 'values' ? 'primary' : 'default'"
          @click="onCharacteristicEmptyValueChange(false)"
        >
          Указать
        </VButton>
      </VButtonGroup>

      <VCheckboxGroup
        option-label="name"
        option-identity="id"

        :data-desired="product && product.getProductCharacteristicById(offerCharacteristic.characteristic.id)?.valueStringRepresentation"
        :class="{ attention: product && offerCharacteristic.value && offerCharacteristic.getMismatchByProduct(product) }"

        :model-value="prepareMultilistValue(offerCharacteristic.value)"

        :options="allowedValuesWithoutEmpty"
        :required="offerCharacteristic.validations.isRequired === true && emptyListValue === false"
        :disabled="selectableMultiState !== 'values' || offerCharacteristic.value === null"

        :layout="layout"

        @update:modelValue="onInputCheckboxGroup($event, offerCharacteristic.characteristic.allowedValues)"
      />
    </template>

    <template v-else>
      <VCheckboxGroup
        v-model="offerCharacteristic.value"

        :label="offerCharacteristic.name"

        :class="{ attention: product && offerCharacteristic.value && offerCharacteristic.getMismatchByProduct(product) }"
        :data-desired="product && product.getProductCharacteristicById(offerCharacteristic.characteristic.id)?.valueStringRepresentation"

        :options="offerCharacteristic.allowedValues"
        option-label="name"
        :required="offerCharacteristic.validations.isRequired"
        :layout="layout"
        :label-width="labelWidth"
      />
    </template>

    <template #helper-text>{{ offerCharacteristic.characteristic.description }}</template>
  </VFormItem>
</template>

<style lang="scss" src="./OfferCharacteristicsFormItem.scss" />

<script lang="ts">
import type OfferCharacteristic from '@/domains/Offers/OfferCharacteristic';
import type Product from "@/domains/Products/Product";
import type EntityPointer from "@/domains/EntityPointer/EntityPointer";
import {UnwrapNestedRefs} from "vue";

export default defineComponent({
  name: 'OfferCharacteristicFormItem',

  props: {
    offerCharacteristic:      {
      type:     Object as PropType<OfferCharacteristic | UnwrapNestedRefs<OfferCharacteristic>>,
      required: true
    },

    product: {
      type: Object as PropType<Product>,
      default: null
    },


    layout: {
      type:    String,
      default: 'vertical'
    },
    labelWidth: {
      type: String,
      default: null,
    }
  },

  data() {
    return {
      allowedValuesSource: this.$core.loader.select(({ pattern }) => {
        if (!pattern) {
          return this.offerCharacteristic.allowedValues;
        }

        return this.offerCharacteristic.allowedValues.filter(item => {
          return item.name.toLowerCase().includes(pattern.toLowerCase());
        });
      }).preloadOnMount()
    }
  },

  computed: {
    /** Указатели на файлы картинки из значения характеристики продукта. */
    picturePointers(): EntityPointer[] {
      const productChar = this.product?.getProductCharacteristicById(this.offerCharacteristic.characteristic.id);

      if (productChar === null || productChar === undefined) {
        return [];
      }

      return productChar.value;
    },

    selectableMultiState(): 'empty' | 'none' | 'values' {
      if (this.offerCharacteristic.value === null) return 'empty';
      if (Array.isArray(this.offerCharacteristic.value) && this.offerCharacteristic.value.length > 0
        && this.offerCharacteristic.value[0].id === this.emptyListValue?.id) return 'none';
      return 'values';
    },

    emptyListValue() {
      if (this.offerCharacteristic.valueType !== 'selectableMulti') return null;
      return this.characteristic.allowedValues.find(v => v.name === 'Нет') ?? null;
    },

    allowedValuesWithoutEmpty() {
      return this.characteristic.allowedValues.filter(v => v.name !== 'Нет');
    },

    characteristic() {
      return this.offerCharacteristic?.characteristic;
    },
  },

  methods: {
    onInputCheckboxGroup(value, allowedValues) {
      this.offerCharacteristic.value = allowedValues.filter(item => value.includes(item.id));
    },

    prepareMultilistValue(values) {
      if (!values) return [];
      return values.map(value => value.id);
    },

    onCharacteristicEmptyValueChange(emptySelected: boolean | null): void {
      switch (emptySelected) {
        case null:
          this.offerCharacteristic.value = null;
          break;
        case true:
          this.offerCharacteristic.value = [this.emptyListValue];
          break;
        case false:
          this.offerCharacteristic.value = [];
          break;
      }
    },
  }
});
</script>
